import { Button, LinearProgress } from "@material-ui/core";
import React from "react";
import styled from "styled-components";

import { ActionPermissionsObject } from "common/interfaces";
import { getStaticUrl } from "js/config";
import WorkspaceController from "js/controllers/WorkspaceController";
import { openSuggestTemplateForm } from "js/core/utilities/externalLinks";
import { BlueButton } from "js/react/components/UiComponents";
import { themeColors } from "js/react/sharedStyles";
import { t } from "js/i18n";
const ProgressBar = styled(LinearProgress)`
    &&& {        
        width: 200px;
        border-radius: 2px;
        margin: auto;
    }
`;

const CenterAlignContainer = styled.div`
    display: flex;
    align-items: center;
`;

const NoticeContainer = styled.div`
    background: white;
    padding: 80px;
    margin: 0 auto;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;

    & > * {
        margin-bottom: 20px;
        max-width: 525px;
    }

    img {
        max-width: ${({ maxImageWidth = null }) => Math.max(maxImageWidth ?? 0, 400)}px;
        margin-bottom: 20px;
    }

    .title {
        font-size: 24px;
        font-weight: bold;
        color: ${themeColors.darkGray};
        margin-bottom: 10px;
    }

    p {
        font-size: 18px;
        color: ${themeColors.darkGray};
        max-width: 600px;
    }

    a {
        color: ${themeColors.ui_blue};
        font-weight: bold;
        text-decoration: none;
        cursor: pointer;

        &:hover {
            text-decoration: underline;
        }
    }
`;

const ExtraContentContainer = styled.div`
    padding-top: 15px;
`;

const ResourceNoticeContainer = styled.div`
    background: #fff;
    padding: 50px 20px;
    display: flex;
    flex-direction: column;
    align-items: center;
    flex: 1 0 0;
    align-self: stretch;

    .title {
        color: #333;
        text-align: center;
        font-family: "Source Sans Pro";
        font-size: 23px;
        font-style: normal;
        font-weight: 600;
        line-height: 120%;
        letter-spacing: 0.5px;
        margin-bottom: 10px;
    }

    .message {
        color: #666;
        text-align: center;
        font-family: "Source Sans Pro";
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 125%;
        letter-spacing: -0.32px;
        margin-bottom: 30px;
    }
`;

export const Notice = props => {
    let {
        image = "shake",
        imageWidth = null,
        title = "This is the title of the notice. It should be replaced.",
        message,
        children,
        showProgressBar = false
    } = props;

    if (image === "sad") {
        image = getStaticUrl(`/images/bai-bot/bai-bot-sad.png`);
        imageWidth = 60;
    } else if (image === "shake") {
        image = getStaticUrl(`/images/bai-bot/bai-bot-shake.gif`);
        imageWidth = 60;
    }

    return (
        <CenterAlignContainer>
            <NoticeContainer>
                {
                    image &&
                    <img src={image} width={imageWidth} />
                }
                {
                    showProgressBar &&
                    <ProgressBar />
                }
                {
                    title &&
                    <div className="title">{title}</div>
                }
                {
                    message
                }
                {
                    !!children &&
                    <ExtraContentContainer>
                        {children}
                    </ExtraContentContainer>
                }
            </NoticeContainer>
        </CenterAlignContainer>
    );
};

export const ResourceNotice = props => {
    const { image, title, message } = props;

    return (
        <CenterAlignContainer>
            <ResourceNoticeContainer>
                <div className="title">{title}</div>
                <div className="message">{message}</div>
                <img src={image} width={700} />
            </ResourceNoticeContainer>
        </CenterAlignContainer>
    );
};

export const NoMatchNotice = props => {
    props = {
        image: "sad",
        title: "No matches found.",
        ...props,
    };

    return <Notice {...props} />;
};

export const NoMatchTemplatesNotice = ({
    onAddClassicSlide,
}) => {
    return (
        <NoMatchNotice
            message={
                <>
                    <div>Try a different search term or design your slide from scratch with a blank Classic Slide.</div>
                    {WorkspaceController.actionPermissions[ActionPermissionsObject.CLASSIC_SLIDES].use &&
                        <BlueButton
                            style={{ marginBottom: 0 }}
                            onClick={onAddClassicSlide}
                        >
                            Add Classic Slide
                        </BlueButton>
                    }
                    <Button
                        color="primary"
                        onClick={openSuggestTemplateForm}
                    >
                        Suggest a Smart Slide Template
                    </Button>
                </>
            }
        />
    );
};

export const NoMyResourcesThemeNotice = () => {
    return (
        <ResourceNotice
            image={getStaticUrl(`/images/notice-banner/themes-landscape.svg`)}
            title="Keep every slide effortlessly on brand with a custom theme."
            message="Create a theme unique to your brand with colors, fonts and styling — then save it to use with every new deck. Apply your theme to any deck and watch your slides adapt automatically."
        />
    );
};

// TEAM NOTICES

export const NoTeamTemplateNotice = () => (
    <ResourceNotice
        image={getStaticUrl(`/images/notice-banner/team-templates.svg`)}
        title="Create custom templates for your team to start from."
        message="Give your team a head start, with structure and key slides already in place."
    />
);

export const NoTeamSlideNotice = () => (
    <ResourceNotice
        image={getStaticUrl(`/images/notice-banner/team-slides.svg`)}
        title="Create a library of Shared Slides."
        message="Simply create a slide once, and your team can use it infinitely. Update your slide and it’ll update everywhere it’s used automatically."
    />
);

export const SharedSlideNotice = () => (
    <ResourceNotice
        image={getStaticUrl(`/images/notice-banner/team-slide-templates-landscape.svg`)}
        title="Give your team a head start with a custom library of slide templates."
        message="Add content or instructional copy that your team can run with."
    />
);

export const SharedSlideGeneralNotice = () => (
    <ResourceNotice
        image={getStaticUrl(`/images/notice-banner/team-slide-templates-landscape.svg`)}
        title="No templates have been shared with your team yet."
        message="Get a headstart with a custom library of slide templates—with content specific to your work already in place."
    />
);

export const NoTeamAssetNotice = () => (
    <ResourceNotice
        image={getStaticUrl(`/images/notice-banner/team-assets.svg`)}
        title="Create a library of Team Assets."
        message="Add logos and images to keep your team on-brand and up to date with a shared library. Updates will apply anywhere the asset is used."
    />
);

export const NoTeamFoldersNotice = () => {
    return (
        <ResourceNotice
            image={getStaticUrl(`/images/notice-banner/team-folders.svg`)}
            title={t("Organize your work in shared Team Folders.")}
            message={t("Collaborate seamlessly and keep your team's presentations organized and easy to find with Team Folders.")}
        />
    );
};
